import type { Area } from 'react-easy-crop';

import type {
  PhotoUploadCompressionOptions,
  PhotoUploadWatermarkOptions
} from './types';

export async function getBlobDimensions(blob: Blob) {
  return await new Promise<{ width: number; height: number }>(
    (resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        const { naturalWidth: width, naturalHeight: height } = img;
        URL.revokeObjectURL(url);
        resolve({ width, height });
      };

      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };

      img.src = url;
    }
  );
}

export function cropToBlob(
  imageSrc: string,
  crop: Area,
  outputType: 'image/webp' | 'image/jpeg' | 'image/png' = 'image/webp',
  rotation = 0
) {
  return new Promise<Blob>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.src = imageSrc;

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Unable to create canvas context'));
        return;
      }

      if (rotation !== 0) {
        // react-easy-crop reports croppedAreaPixels in the rotated coordinate
        // space. We need to draw the image rotated on a safe-area canvas first,
        // then extract the crop region from that.
        const radians = (rotation * Math.PI) / 180;
        const maxSize = Math.max(image.naturalWidth, image.naturalHeight);
        const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

        canvas.width = safeArea;
        canvas.height = safeArea;

        ctx.translate(safeArea / 2, safeArea / 2);
        ctx.rotate(radians);
        ctx.translate(-safeArea / 2, -safeArea / 2);
        ctx.drawImage(
          image,
          safeArea / 2 - image.naturalWidth / 2,
          safeArea / 2 - image.naturalHeight / 2
        );

        const data = ctx.getImageData(0, 0, safeArea, safeArea);
        canvas.width = crop.width;
        canvas.height = crop.height;
        ctx.putImageData(
          data,
          Math.round(0 - safeArea / 2 + image.naturalWidth / 2 - crop.x),
          Math.round(0 - safeArea / 2 + image.naturalHeight / 2 - crop.y)
        );
      } else {
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        canvas.width = crop.width;
        canvas.height = crop.height;

        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height
        );
      }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Canvas is empty'));
            return;
          }
          resolve(blob);
        },
        outputType,
        1
      );
    };

    image.onerror = (error) => reject(error);
  });
}

export async function compressBlob(
  blob: Blob,
  options: PhotoUploadCompressionOptions = {}
) {
  const {
    maxSizeMB = 1,
    maxWidthOrHeight = 1200,
    initialQuality = 0.9,
    fileType = 'image/webp',
    noCompress = false
  } = options;

  if (noCompress) {
    return blob;
  }

  const extForName =
    fileType === 'image/jpeg'
      ? 'jpg'
      : fileType === 'image/png'
        ? 'png'
        : 'webp';
  const inputFile = new File([blob], `upload.${extForName}`, {
    type: fileType
  });

  const { default: imageCompression } = await import(
    'browser-image-compression'
  );
  const compressed = await imageCompression(inputFile, {
    maxSizeMB,
    maxWidthOrHeight,
    initialQuality,
    fileType,
    useWebWorker: true
  });

  return compressed;
}

export async function applyTextWatermark(
  blob: Blob,
  options: PhotoUploadWatermarkOptions,
  outputType: 'image/webp' | 'image/jpeg' | 'image/png' = 'image/webp'
) {
  const enabled = options.enabled ?? false;
  if (!enabled) return blob;

  const text = options.text?.trim() || 'paction.ca';
  const opacity = options.opacity ?? 0.5;

  return await new Promise<Blob>((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Unable to create canvas context'));
      return;
    }

    const image = new Image();
    const url = URL.createObjectURL(blob);

    image.onload = () => {
      try {
        canvas.width = image.width;
        canvas.height = image.height;

        ctx.drawImage(image, 0, 0, image.width, image.height);

        ctx.save();
        ctx.globalAlpha = opacity;

        const fontSize = Math.min(canvas.width, canvas.height) * 0.15;
        ctx.font = `${fontSize}px sans-serif`;
        ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(-Math.atan(canvas.height / canvas.width));
        ctx.textAlign = 'center';
        ctx.fillText(text, 0, 0);
        ctx.restore();

        canvas.toBlob(
          (next) => {
            URL.revokeObjectURL(url);
            if (!next) {
              reject(new Error('Canvas is empty'));
              return;
            }
            resolve(next);
          },
          outputType,
          1
        );
      } catch (err) {
        URL.revokeObjectURL(url);
        reject(err);
      }
    };

    image.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };

    image.src = url;
  });
}

export async function getCroppedImageBlob(
  imageSrc: string,
  crop: Area,
  compression: PhotoUploadCompressionOptions,
  rotation = 0
) {
  const targetType = compression.fileType ?? 'image/webp';
  const cropped = await cropToBlob(imageSrc, crop, targetType, rotation);
  return await compressBlob(cropped, compression);
}

export function blobToFile(blob: Blob, fileName: string, contentType: string) {
  return new File([blob], fileName, { type: contentType });
}

type SniffableImageMime = 'image/webp' | 'image/jpeg' | 'image/png';

const EXT_BY_IMAGE_MIME: Record<SniffableImageMime, string> = {
  'image/webp': 'webp',
  'image/jpeg': 'jpg',
  'image/png': 'png'
};

/**
 * Inspect the leading magic bytes of an encoded image blob to determine its
 * TRUE format. Browsers silently fall back to PNG when `canvas.toBlob` (and
 * therefore `browser-image-compression`) cannot encode the requested type —
 * e.g. WebP on some Safari/iOS versions. Trusting the requested `fileType` in
 * that case stores mislabeled objects (PNG bytes named `.webp` and served as
 * `image/webp`) that skip compression and render blank in strict browsers.
 * Always sniff the bytes that were actually produced.
 */
export async function sniffImageMimeType(
  blob: Blob
): Promise<SniffableImageMime | null> {
  const header = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
  if (header.length < 12) return null;

  // PNG: 89 50 4E 47
  if (
    header[0] === 0x89 &&
    header[1] === 0x50 &&
    header[2] === 0x4e &&
    header[3] === 0x47
  ) {
    return 'image/png';
  }

  // JPEG: FF D8 FF
  if (header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff) {
    return 'image/jpeg';
  }

  // WebP: "RIFF" .... "WEBP"
  if (
    header[0] === 0x52 &&
    header[1] === 0x49 &&
    header[2] === 0x46 &&
    header[3] === 0x46 &&
    header[8] === 0x57 &&
    header[9] === 0x45 &&
    header[10] === 0x42 &&
    header[11] === 0x50
  ) {
    return 'image/webp';
  }

  return null;
}

export type ProcessedImageUpload = {
  file: File;
  fileName: string;
  contentType: SniffableImageMime;
};

/**
 * Build upload metadata for a processed image blob from its ACTUAL encoded
 * format rather than the requested one, so the stored object's extension and
 * Content-Type always match its bytes. WebP stays the default for every
 * browser that can encode it; browsers that silently fell back to PNG/JPEG get
 * a correctly-labeled file that still renders instead of a blank `.webp`.
 */
export async function resolveProcessedImageUpload(
  blob: Blob,
  options?: {
    namePrefix?: string;
    fallbackType?: SniffableImageMime;
  }
): Promise<ProcessedImageUpload> {
  const contentType =
    (await sniffImageMimeType(blob)) ?? options?.fallbackType ?? 'image/webp';
  const ext = EXT_BY_IMAGE_MIME[contentType];

  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const prefix = options?.namePrefix ? `${options.namePrefix}-` : '';
  const fileName = `${prefix}${uuid}.${ext}`;

  return {
    file: new File([blob], fileName, { type: contentType }),
    fileName,
    contentType
  };
}
